import { Component, OnInit, Input, OnChanges, SimpleChanges, ViewChild, TemplateRef, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TableComponent, TableColumn, PaginationConfig } from '../primeng';
import { FilterPanelComponent, FilterField } from '../filter-panel/filter-panel.component';
import { DateRange } from '../primeng/date-range-picker/date-range-picker.component';
import { ActivitiesApiService, ActivityResponse } from '@angular-workspace/services';
import { FormatService } from '../../services/format.service';
import { FilterEnumsService } from '../../services/filter-enums.service';

export interface ActivityData {
  assetName: string;
  assetType: string;
  activity: string;
  accessedAt: string;
  /** Present when logType is COMPUTE (My Activities with tabs) */
  userName?: string;
  role?: string;
}

export interface ActivityFilters {
  type: string[];
  activity: string[];
  dateRange: DateRange | null;
}

@Component({
  selector: 'app-activities',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableComponent,
    FilterPanelComponent
  ],
  templateUrl: './activities.component.html',
  styleUrl: './activities.component.scss',
})
export class ActivitiesComponent implements OnInit, OnChanges, AfterViewInit {
  @Input() showFilters: boolean = true;
  @Input() pageSize: number = 10;
  @Input() showPagination: boolean = true;
  @Input() disableSorting: boolean = false;
  /** When true, shows tabs: Assets, Compute, User action (and passes logType to API when loading) */
  @Input() showTabs: boolean = false;
  /** When true, show only Assets and User action tabs (no Compute tab). Use for platform-admin. */
  @Input() hideComputeTab: boolean = false;
  /**
   * When true, use admin auditing endpoint (`.../auditing/admin/activity`).
   * When false, use consumer endpoint (`.../auditing/consumer/activity`).
   */
  @Input() useAdminAuditingApi: boolean = false;

  @ViewChild('activityCell', { static: false }) activityCellTemplate!: TemplateRef<any>;

  /** Active tab when showTabs is true: ASSET | COMPUTE | USER_ACTION */
  activeLogType: 'ASSET' | 'COMPUTE' | 'USER_ACTION' = 'ASSET';

  filterFields: FilterField[] = [];

  constructor(
    private activitiesApiService: ActivitiesApiService,
    private formatService: FormatService,
    private filterEnumsService: FilterEnumsService
  ) {
    this.initFilterFields();
  }

  private initFilterFields(): void {
    this.filterFields = [
      {
        key: 'type',
        label: 'Asset Type',
        type: 'multiselect',
        placeholder: 'Choose Type',
        options: this.filterEnumsService.getAssetTypeOptions(),
        value: []
      },
      {
        key: 'activity',
        label: 'Activity',
        type: 'multiselect',
        placeholder: 'Choose Activity',
        options: this.filterEnumsService.getActivityOptions(),
        value: []
      },
      {
        key: 'dateRange',
        label: 'Date Range',
        type: 'dateRange',
        placeholder: 'Choose Date Range',
        value: null
      }
    ];
  }

  filters: ActivityFilters = {
    type: [],
    activity: [],
    dateRange: null
  };

  /** Columns for Assets / User action tabs */
  private assetColumns: TableColumn[] = [
    { key: 'assetName', label: 'Asset Name', sortable: true, width: '30%' },
    { key: 'assetType', label: 'Asset Type', sortable: false, width: '20%' },
    { key: 'activity', label: 'Activity', sortable: true, width: '25%' },
    { key: 'accessedAt', label: 'Accessed At', sortable: true, width: '25%', align: 'left' }
  ];

  /** Columns for Compute tab (My Activities with tabs): UserName, Role, Activity, Accessed At */
  private computeColumns: TableColumn[] = [
    { key: 'userName', label: 'User Name', sortable: true, width: '30%' },
    { key: 'role', label: 'Role', sortable: false, width: '25%' },
    { key: 'activity', label: 'Activity', sortable: true, width: '25%' },
    { key: 'accessedAt', label: 'Accessed At', sortable: true, width: '20%', align: 'left' }
  ];

  /** Compute tab activity filter options (alphabetical, Pascal case) */
  private readonly computeActivityOptions = ['Create', 'Delete', 'Start', 'Stop'];

  /** User action tab activity filter options (same as platform-admin User Activities) */
  private readonly userActionActivityOptions = [
    'Like',
    'Dislike',
    'Bookmark',
    'Unbookmark',
  ];

  /**
   * When no Activity filter is selected, repeat each value as `action` query params (matches admin API:
   * e.g. logType=COMPUTE → action=Create&action=Delete&…).
   */
  private getDefaultActionsForLogType(logType: 'ASSET' | 'COMPUTE' | 'USER_ACTION'): string[] {
    if (logType === 'COMPUTE') {
      return [...this.computeActivityOptions];
    }
    if (logType === 'USER_ACTION') {
      return [...this.userActionActivityOptions];
    }
    return this.filterEnumsService.getActivityOptions().map((a) => this.formatService.toPascalCase(a));
  }

  get columns(): TableColumn[] {
    return this.showTabs && this.activeLogType === 'COMPUTE' ? this.computeColumns : this.assetColumns;
  }

  /** Cached filter fields for template (avoids new array every CD which can freeze UI when switching to Compute tab) */
  effectiveFilterFields: FilterField[] = [];

  /** Update effectiveFilterFields when tab or filter config changes; call after activeLogType/showTabs/filterFields change. */
  private updateEffectiveFilterFields(): void {
    if (!this.showTabs) {
      this.effectiveFilterFields = this.filterFields;
      return;
    }
    if (this.activeLogType === 'COMPUTE') {
      this.effectiveFilterFields = this.filterFields
        .filter(f => f.key !== 'type')
        .map(f => {
          if (f.key === 'activity') {
            return { ...f, options: [...this.computeActivityOptions] };
          }
          return f;
        });
      return;
    }
    if (this.activeLogType === 'USER_ACTION') {
      this.effectiveFilterFields = this.filterFields.map(f => {
        if (f.key === 'activity') {
          return { ...f, options: [...this.userActionActivityOptions] };
        }
        return f;
      });
      return;
    }
    // ASSET tab: use default filter fields
    this.effectiveFilterFields = this.filterFields;
  }

  allActivitiesData: ActivityData[] = [];
  activitiesData: ActivityData[] = [];

  pagination: PaginationConfig = {
    enabled: true,
    currentPage: 1,
    pageSize: 10,
    totalItems: 0,
    pageSizeOptions: [10, 25, 50, 100],
    serverSide: true
  };

  loading = false;
  emptyMessage = 'No Activities Found';
  private initialized = false;

  /** Current sort state for server-side sorting */
  sortColumn: string | null = null;
  sortDirection: 'asc' | 'desc' = 'asc';

  setLogType(logType: 'ASSET' | 'COMPUTE' | 'USER_ACTION'): void {
    if (this.activeLogType === logType) return;
    this.activeLogType = logType;
    this.updateEffectiveFilterFields();
    this.pagination = { ...this.pagination, currentPage: 1 };
    this.loadActivities();
  }

  /**
   * Get activity class for styling based on activity type
   */
  getActivityClass(activity: string): string {
    const activityLower = activity?.toLowerCase() || '';
    
    if (activityLower.includes('view') || activityLower === 'view') {
      return 'activity-view';
    } else if (activityLower.includes('create') || activityLower === 'create') {
      return 'activity-create';
    } else if (activityLower.includes('update') || activityLower === 'update') {
      return 'activity-update';
    } else if (activityLower.includes('delete') || activityLower === 'delete') {
      return 'activity-delete';
    } else if (activityLower.includes('download') || activityLower === 'download') {
      return 'activity-download';
    } else if (activityLower.includes('submit') || activityLower === 'submit') {
      return 'activity-submit';
    } else if (activityLower.includes('approve') || activityLower === 'approve') {
      return 'activity-approve';
    } else if (activityLower.includes('reject') || activityLower === 'reject') {
      return 'activity-reject';
    } else if (activityLower.includes('like') || activityLower === 'like') {
      return 'activity-like';
    } else if (activityLower.includes('bookmark') || activityLower === 'bookmark') {
      return 'activity-bookmark';
    } else if (activityLower.includes('dislike') || activityLower === 'dislike') {
      return 'activity-dislike';
    }
    
    return 'activity-default';
  }

  ngOnInit(): void {
    this.updateEffectiveFilterFields();
    this.initializeAndLoad();
  }

  private initializeAndLoad(): void {
    if (this.initialized) {
      return;
    }

    this.initialized = true;
    
    // Initialize columns with sorting based on disableSorting input
    this.updateColumnsSorting();
    
    // Initialize pagination with input values
    this.pagination = {
      enabled: this.showPagination,
      currentPage: 1,
      pageSize: this.pageSize,
      totalItems: 0,
      pageSizeOptions: [10, 25, 50, 100],
      serverSide: true
    };
    
    this.loadActivities();
  }

  private updateColumnsSorting(): void {
    const sortable = !this.disableSorting;
    this.assetColumns = [
      { key: 'assetName', label: 'Asset Name', sortable, width: '30%' },
      { key: 'assetType', label: 'Asset Type', sortable: false, width: '20%' },
      { key: 'activity', label: 'Activity', sortable, width: '25%' },
      { key: 'accessedAt', label: 'Accessed At', sortable, width: '25%', align: 'left' }
    ];
    this.computeColumns = [
      { key: 'userName', label: 'User Name', sortable, width: '30%' },
      { key: 'role', label: 'Role', sortable: false, width: '25%' },
      { key: 'activity', label: 'Activity', sortable, width: '25%' },
      { key: 'accessedAt', label: 'Accessed At', sortable, width: '20%', align: 'left' }
    ];
  }

  ngAfterViewInit(): void {
    // Assign activity cell template to activity column in both column sets
    [this.assetColumns, this.computeColumns].forEach(cols => {
      const activityCol = cols.find(col => col.key === 'activity');
      if (activityCol && this.activityCellTemplate) {
        activityCol.cellTemplate = this.activityCellTemplate;
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Update columns sorting if disableSorting changes
    if (changes['disableSorting']) {
      this.updateColumnsSorting();
    }
    // Update filter fields when showTabs changes (e.g. Compute tab filter set)
    if (changes['showTabs']) {
      this.updateEffectiveFilterFields();
    }

    // Update pagination when pageSize or showPagination changes
    if (changes['pageSize'] || changes['showPagination']) {
      this.pagination = {
        ...this.pagination,
        pageSize: this.pageSize,
        enabled: this.showPagination
      };
      
      // Check if any of the changed inputs are first changes
      const isFirstChange = (changes['pageSize'] && changes['pageSize'].firstChange) || 
                           (changes['showPagination'] && changes['showPagination'].firstChange) ||
                           (changes['showFilters'] && changes['showFilters'].firstChange);
      
      if (isFirstChange && !this.initialized) {
        // This is the first change (fresh component), so initialize here
        // This handles the case where ngOnChanges is called before ngOnInit
        setTimeout(() => {
          if (!this.initialized) {
            this.initializeAndLoad();
          }
        }, 0);
      } else if (!isFirstChange) {
        // Reload activities with new page size if not initial change (component reused)
        this.pagination = { ...this.pagination, currentPage: 1 };
        this.loadActivities();
      }
    }
  }

  /**
   * Map filter display values to API format
   * @param filterValue Display value (e.g., "Dataset")
   * @returns API format value (e.g., "DATABANK")
   */
  private mapAssetTypeToApiFormat(filterValue: string): string {
    return this.filterEnumsService.mapAssetTypeToApi(filterValue);
  }

  /**
   * Normalise date range to { startDate, endDate } for formatDateRangeForApi (handles start/end or startDate/endDate).
   * Activity API receives: time=YYYY-MM-DDTHH:mm:ss&endtime=YYYY-MM-DDTHH:mm:ss&timerel=between
   */
  private getDateRangeForApi(dateRange: any): { startDate: string; endDate: string } | undefined {
    if (!dateRange) return undefined;
    const start = dateRange.startDate ?? dateRange.start;
    const end = dateRange.endDate ?? dateRange.end;
    if (!start || !end) return undefined;
    return this.formatService.formatDateRangeForApi({ startDate: start, endDate: end });
  }

  /**
   * Get activity value for API (action param). Handles string or option object – returns PascalCase (e.g. Approve, Create).
   */
  private getActivityActionValue(item: any): string | null {
    if (item == null) return null;
    if (typeof item === 'string' && item.trim()) return this.formatService.toPascalCase(item.trim());
    if (typeof item === 'object' && item !== null) {
      const raw = (item as { value?: string; label?: string }).value ?? (item as { value?: string; label?: string }).label;
      if (typeof raw === 'string' && raw.trim()) return this.formatService.toPascalCase(raw.trim());
    }
    return null;
  }

  async loadActivities(): Promise<void> {
    this.loading = true;
    try {
      // Map filter values to API format
      const entityTypes = this.filters.type && this.filters.type.length > 0
        ? this.filters.type.map(type => this.mapAssetTypeToApiFormat(type))
        : undefined;

      // Map activity filter values to action param (PascalCase: Approve → &action=Approve, Create → &action=Create)
      let operations =
        this.filters.activity && this.filters.activity.length > 0
          ? this.filters.activity
              .map((activity) => this.getActivityActionValue(activity))
              .filter((v): v is string => v != null && v !== '')
          : undefined;

      if (this.showTabs && (!operations || operations.length === 0)) {
        operations = this.getDefaultActionsForLogType(this.activeLogType);
      }

      // Date range for API: time=YYYY-MM-DDTHH:mm:ss&endtime=YYYY-MM-DDTHH:mm:ss&timerel=between
      const formattedDateRange = this.getDateRangeForApi(this.filters.dateRange);
      const startDate = formattedDateRange?.startDate;
      const endDate = formattedDateRange?.endDate;

      // Build sort query param (fieldName:asc or fieldName:desc)
      // Map UI column keys to API field names (Compute tab uses userName, role)
      const columnToApiField: Record<string, string> = this.showTabs && this.activeLogType === 'COMPUTE'
        ? { activity: 'action', accessedAt: 'createdAt', userName: 'userName', role: 'role' }
        : { activity: 'action', accessedAt: 'createdAt' };
      const apiSortField = this.sortColumn
        ? (columnToApiField[this.sortColumn] ?? this.sortColumn)
        : null;
      const sortParam = apiSortField
        ? `${apiSortField}:${this.sortDirection}`
        : undefined;

      // Pass logType for consumer/activity API: active tab when showTabs, else default ASSET
      const logTypeParam = this.showTabs ? this.activeLogType : 'ASSET';

      // Use this.pageSize from input instead of pagination.pageSize
      // Retry once on 401 to handle token race on first load (token not yet in headers)
      let response: ActivityResponse;
      try {
        response = this.useAdminAuditingApi
          ? await this.activitiesApiService.getAllPlatformUserActivities(
              this.pagination.currentPage,
              this.pageSize,
              entityTypes,
              operations,
              startDate,
              endDate,
              sortParam,
              logTypeParam
            )
          : await this.activitiesApiService.getUserActivities(
              this.pagination.currentPage,
              this.pageSize,
              entityTypes,
              operations,
              startDate,
              endDate,
              sortParam,
              logTypeParam
            );
      } catch (err: any) {
        if (err?.status === 401) {
          await new Promise((r) => setTimeout(r, 500));
          response = this.useAdminAuditingApi
            ? await this.activitiesApiService.getAllPlatformUserActivities(
                this.pagination.currentPage,
                this.pageSize,
                entityTypes,
                operations,
                startDate,
                endDate,
                sortParam,
                logTypeParam
              )
            : await this.activitiesApiService.getUserActivities(
                this.pagination.currentPage,
                this.pageSize,
                entityTypes,
                operations,
                startDate,
                endDate,
                sortParam,
                logTypeParam
              );
        } else {
          throw err;
        }
      }
      
      const isComputeTab = this.showTabs && this.activeLogType === 'COMPUTE';
      const mapComputeItem = (item: any): ActivityData => ({
        assetName: '',
        assetType: '',
        userName: item.userName || item.user_name || item.userId || 'Unknown User',
        role: item.role ? (item.role.charAt(0).toUpperCase() + item.role.slice(1).toLowerCase()) : '',
        activity: this.formatService.toPascalCase(item.action || item.activity || ''),
        accessedAt: this.formatService.formatDate(item.createdAt || item.accessedAt || item.accessed_at || item.timestamp || '')
      });
      const mapAssetItem = (item: any): ActivityData => {
        const rawAssetType = item.entityType || item.assetType || item.asset_type || item.type || '';
        let formattedAssetType = rawAssetType ? this.formatService.formatAssetType(rawAssetType) : '';
        if (formattedAssetType && formattedAssetType.toUpperCase().trim() === 'DATABANK') {
          formattedAssetType = 'Dataset';
        }
        return {
          assetName: item.entityName || item.assetName || item.asset_name || item.name || '',
          assetType: formattedAssetType,
          activity: this.formatService.toPascalCase(item.action || item.activity || ''),
          accessedAt: this.formatService.formatDate(item.createdAt || item.accessedAt || item.accessed_at || item.timestamp || '')
        };
      };
      const mapItem = (item: any): ActivityData => isComputeTab ? mapComputeItem(item) : mapAssetItem(item);

      let mappedData: ActivityData[] = [];
      if (response && response['result'] && Array.isArray(response['result'])) {
        const dataArray = response['result'];
        mappedData = dataArray.map((item: any) => mapItem(item));
        
        // Update pagination from paginationInfo
        if (response['paginationInfo'] && response['paginationInfo']['totalCount'] !== undefined) {
          this.pagination = { 
            ...this.pagination, 
            totalItems: response['paginationInfo']['totalCount'] 
          };
        }
      } else if (response && response['data'] && Array.isArray(response['data'])) {
        // Fallback: Handle legacy response structure with 'data' property
        const dataArray = response['data'];
        mappedData = dataArray.map((item: any) => mapItem(item));
        
        if (response['total'] !== undefined) {
          this.pagination = { ...this.pagination, totalItems: response['total'] };
        } else if (response['count'] !== undefined) {
          this.pagination = { ...this.pagination, totalItems: response['count'] };
        }
      } else if (Array.isArray(response)) {
        // Fallback: Handle direct array response
        mappedData = response.map((item: any) => mapItem(item));
        this.pagination = { ...this.pagination, totalItems: mappedData.length };
      } else {
        // 204 No Content or empty response – no result/data/array; ensure pagination shows zero items
        this.pagination = { ...this.pagination, totalItems: 0 };
      }
      
      this.activitiesData = mappedData;
      this.allActivitiesData = [...mappedData];
      
      if (mappedData.length === 0) {
        this.emptyMessage = 'No Activities Found';
      }
    } catch (error) {
      console.error('Error loading activities:', error);
      this.activitiesData = [];
      this.allActivitiesData = [];
      this.pagination = { ...this.pagination, totalItems: 0 };
      this.emptyMessage = 'No Activities Found';
    } finally {
      this.loading = false;
    }
  }

  onPageChange(page: number): void {
    this.pagination = { ...this.pagination, currentPage: page };
    this.loadActivities();
  }

  onPageSizeChange(pageSize: number): void {
    // Only update if pagination is enabled (not in dashboard mode)
    if (this.showPagination) {
      this.pageSize = pageSize;
      this.pagination = { ...this.pagination, pageSize: pageSize, currentPage: 1 };
      this.loadActivities();
    }
  }

  onSortChange(event: { column: string; direction: 'asc' | 'desc' }): void {
    this.sortColumn = event.column;
    this.sortDirection = event.direction;
    // Reset to first page when sort changes
    this.pagination = { ...this.pagination, currentPage: 1 };
    this.loadActivities();
  }

  onFilterChange(filterValues: Record<string, any>): void {
    this.filters = {
      type: Array.isArray(filterValues['type']) ? filterValues['type'] : [],
      activity: Array.isArray(filterValues['activity']) ? filterValues['activity'] : [],
      dateRange: filterValues['dateRange'] || null
    };
    // Normalise activity to strings for API (action=Approve, action=Create etc.)
    this.filters.activity = this.filters.activity
      .map((a: any) => this.getActivityActionValue(a))
      .filter((v): v is string => v != null && v !== '');
    // Reset to first page when filters change
    this.pagination = { ...this.pagination, currentPage: 1 };
    // Reload activities with new filters
    this.loadActivities();
  }

  onClearFilters(): void {
    this.filters = { type: [], activity: [], dateRange: null };
    // Reset filter field values
    this.filterFields.forEach(field => {
      if (field.type === 'multiselect') {
        field.value = [];
      } else if (field.type === 'dateRange') {
        field.value = null;
      } else {
        field.value = '';
      }
    });
    // Reset to first page and reload activities
    this.pagination = { ...this.pagination, currentPage: 1 };
    this.loadActivities();
  }
}
